import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { stripeApi, type ConnectStatus } from "../lib/api";
import { ArrowLeftIcon } from "../components/ui/Icons";

export function PaymentSettingsPage() {
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [searchParams] = useSearchParams();

  const loadStatus = async () => {
    try {
      const data = await stripeApi.connectStatus();
      setStatus(data);
    } catch {
      // Stripe not configured — show setup state
      setStatus({ connected: false, chargesEnabled: false, onboardingComplete: false });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Refresh status when returning from Stripe onboarding
  useEffect(() => {
    if (searchParams.get("onboarding") === "complete") {
      loadStatus();
    }
  }, [searchParams]);

  const handleOnboard = async () => {
    setIsOnboarding(true);
    try {
      const result = await stripeApi.connectOnboard();
      if (result.url) {
        window.location.href = result.url;
      } else if (result.alreadyComplete) {
        await loadStatus();
      }
    } catch (err) {
      console.error("Failed to start onboarding:", err);
    } finally {
      setIsOnboarding(false);
    }
  };

  const handleDashboard = async () => {
    try {
      const result = await stripeApi.dashboardLink();
      window.open(result.url, "_blank");
    } catch (err) {
      console.error("Failed to get dashboard link:", err);
    }
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-3xl px-4 pb-20 sm:px-6 lg:px-8">
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-secondary"
          >
            <ArrowLeftIcon size={16} />
            Back to Dashboard
          </Link>

          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
              Payment <span className="text-amber">Settings</span>
            </h1>
            <p className="mt-2 text-muted">
              Connect your Stripe account to sell plugins and receive payouts
            </p>
          </div>

          {isLoading ? (
            <div className="card-glow animate-pulse p-8">
              <div className="mb-4 h-6 w-48 rounded bg-elevated/50" />
              <div className="mb-2 h-4 w-full rounded bg-elevated/50" />
              <div className="h-4 w-3/4 rounded bg-elevated/50" />
            </div>
          ) : status?.onboardingComplete ? (
            <div className="card-glow p-8">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10">
                  <svg
                    className="h-5 w-5 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold text-primary">Stripe Connected</h2>
                  <p className="text-sm text-muted">
                    Your account is set up and ready to receive payments
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-elevated bg-surface/50 p-4">
                <h3 className="mb-2 font-mono text-sm font-medium text-secondary">
                  What you can do now
                </h3>
                <ul className="space-y-2 text-sm text-muted">
                  <li>Set prices on your plugins from your dashboard</li>
                  <li>View your earnings and sales history</li>
                  <li>Manage payouts through the Stripe dashboard</li>
                </ul>
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={handleDashboard} className="btn-primary">
                  Open Stripe Dashboard
                </button>
                <Link
                  to="/dashboard/earnings"
                  className="rounded-lg border border-elevated px-4 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                >
                  View Earnings
                </Link>
              </div>
            </div>
          ) : (
            <div className="card-glow p-8">
              <h2 className="mb-2 font-display text-lg font-bold text-primary">
                Start Selling Plugins
              </h2>
              <p className="mb-6 text-muted">
                Connect your Stripe account to set prices on your plugins and receive payouts. The
                platform takes a 15% fee — you keep 85% of every sale.
              </p>

              <div className="mb-6 rounded-lg border border-elevated bg-surface/50 p-4">
                <h3 className="mb-2 font-mono text-sm font-medium text-secondary">How it works</h3>
                <ol className="list-decimal space-y-1 pl-4 text-sm text-muted">
                  <li>Connect your Stripe account (takes ~2 minutes)</li>
                  <li>Set prices on any of your published plugins</li>
                  <li>Buyers purchase through Stripe Checkout</li>
                  <li>You receive 85% of each sale via Stripe payouts</li>
                </ol>
              </div>

              <button
                onClick={handleOnboard}
                disabled={isOnboarding}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {isOnboarding ? "Redirecting to Stripe..." : "Connect with Stripe"}
              </button>

              {status?.connected && !status?.chargesEnabled && (
                <p className="mt-4 text-sm text-amber">
                  Your Stripe account is partially set up. Click above to complete onboarding.
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
